import type { CSSProperties } from 'react'

import { NO_VALUE } from '../../../lib/utils'
import { useUnits } from '../../../lib/UnitsProvider'
import { TOKENS } from '../../ui/dashboard-primitives'
import { ArtifactCard } from './ArtifactCard'
import { ARTIFACT_CHIP_NEUTRAL, ARTIFACT_CHIP_WARN, ARTIFACT_NAME, ARTIFACT_TITLE } from './styles'
import type { SessionArtifactData } from './types'

const ROW: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'baseline',
  gap: 14,
  padding: '6px 0',
  fontSize: 13,
  color: TOKENS.creamSoft,
}

/** A rule between data rows, not a section divider — the No-Line Rule governs
 *  sectioning, and a prescribed-weight table is exactly where a row rule reads
 *  as structure rather than as a box. Value taken from the mockup. */
const ROW_DIVIDED: CSSProperties = {
  ...ROW,
  borderTop: '1px solid ' + TOKENS.creamHairline,
}

const VALUE: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontWeight: 500,
  color: TOKENS.cream,
  fontVariantNumeric: 'tabular-nums',
}

/** Section marker for a block. The No-Line Rule bans a rule between sections,
 *  so the eyebrow plus its top margin IS the separation. */
const BLOCK_EYEBROW: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  color: TOKENS.creamDim,
  marginTop: 14,
  marginBottom: 4,
}

/**
 * `session` — one of the user's own workout-builder sessions: its name, how
 * long since it was last trained, and its blocks (cardio, strength, mobility)
 * with the strength block's prescribed exercises and target weights.
 *
 * Only the deterministic opener supplies `recency_label`
 * (`_opener_artifacts` in api/routers/coach.py); `show_session` passes None.
 * Its presence is therefore what makes this "up next" rather than "a plan
 * card", and the eyebrow says so.
 */
export function SessionArtifactCard({ data }: { data: SessionArtifactData }) {
  const { formatLoad } = useUnits()
  return (
    <ArtifactCard
      eyebrow={data.recency_label ? 'Up next · Session' : 'Session · Plan'}
      action={data.href ? { label: 'Open →', href: data.href } : null}
      cardType="session"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span style={ARTIFACT_TITLE}>{data.title}</span>
        {data.recency_label ? (
          <span style={data.recency_tone === 'warn' ? ARTIFACT_CHIP_WARN : ARTIFACT_CHIP_NEUTRAL}>
            {data.recency_label}
          </span>
        ) : null}
      </div>
      {data.blocks.length > 0 ? (
        data.blocks.map((block, blockIndex) => (
          <div key={`${block.type}-${blockIndex}`}>
            <div style={BLOCK_EYEBROW} data-testid="session-block-eyebrow">
              {block.type}
            </div>
            {block.type === 'strength' ? (
              <div className="grid">
                {block.rows.map((row, index) => (
                  <div key={`${row.name}-${index}`} style={index === 0 ? ROW : ROW_DIVIDED}>
                    <span style={ARTIFACT_NAME}>{row.name}</span>
                    <b style={VALUE}>
                      {row.target_weight_kg === null ? NO_VALUE : formatLoad(row.target_weight_kg)}
                    </b>
                  </div>
                ))}
              </div>
            ) : (
              <div style={ROW}>
                <span style={ARTIFACT_NAME}>{block.label}</span>
                {block.detail ? <b style={VALUE}>{block.detail}</b> : null}
              </div>
            )}
          </div>
        ))
      ) : data.rows.length > 0 ? (
        <div className="grid" style={{ marginTop: 10 }}>
          {data.rows.map((row, index) => (
            <div key={`${row.name}-${index}`} style={index === 0 ? ROW : ROW_DIVIDED}>
              <span style={ARTIFACT_NAME}>{row.name}</span>
              <b style={VALUE}>
                {row.target_weight_kg === null ? NO_VALUE : formatLoad(row.target_weight_kg)}
              </b>
            </div>
          ))}
        </div>
      ) : null}
    </ArtifactCard>
  )
}
